import { useState, useEffect, useCallback } from "react";
import { FixedSizeList } from "react-window";
import { db } from "../lib/db";
import { fmt12 } from "../utils/chartHelpers";
import { NoteCardSkeleton } from "../components/Skeleton";

// ─── NOTE TYPE SYSTEM (duplicated here for standalone use) ────────────────────
const NOTE_TYPE_GROUPS = [
  { category: "Clinical", badge: "badge-teal", types: [
    "New Patient Consultation", "Follow-Up Visit", "Post-Operative Visit",
    "Injection Visit", "Workers Comp Evaluation",
    "Independent Medical Examination (IME)", "Urgent/Same-Day Visit",
  ]},
  { category: "Operative", badge: "badge-blue", types: [
    "Operative Report", "Procedure Note", "Anesthesia Note",
  ]},
  { category: "Communication", badge: "badge-amber", types: [
    "Phone Call — Inbound", "Phone Call — Outbound", "Patient Message Response",
    "Family/Guardian Communication", "Attorney Communication",
    "Insurance Communication", "Referring Provider Communication",
  ]},
  { category: "Administrative", badge: "badge-purple", types: [
    "Prior Authorization Note", "Refill Authorization", "FMLA/Disability Note",
    "Work Status Note", "Letter of Medical Necessity", "Incident Report",
  ]},
  { category: "Other", badge: "badge-gray", types: [
    "Nursing Note", "MA Note", "General Note",
  ]},
];

function getNoteTypeBadge(noteType) {
  for (const g of NOTE_TYPE_GROUPS) {
    if (g.types.includes(noteType)) return g.badge;
  }
  return "badge-gray";
}

// ─── NOTES PAGE ───────────────────────────────────────────────────────────────
export default function NotesPage({ patients, onPatientSelect, initialFilter, practiceId }) {
  const [notes, setNotes]         = useState([]);
  const [patientMap, setPatientMap] = useState({});
  const [loading, setLoading]     = useState(true);
  const [selected, setSelected]   = useState(null);
  const [filter, setFilter]       = useState(initialFilter || "all");

  useEffect(() => {
    async function load() {
      // Step 1 — fetch notes (no join; avoids FK / RLS issues on patients table)
      const notesQuery = db
        .from("clinical_notes")
        .select("*")
        .order("visit_date", { ascending: false })
        .limit(100);
      if (practiceId) notesQuery.eq("practice_id", practiceId);

      const { data: notesData, error: notesError } = await notesQuery;

      if (notesError) {
        console.error("NotesPage fetch error:", notesError);
        setLoading(false);
        return;
      }

      setNotes(notesData || []);

      // Step 2 — fetch patient names for every unique patient_id in the result
      const uniqueIds = [...new Set((notesData || []).map(n => n.patient_id).filter(Boolean))];
      if (uniqueIds.length > 0) {
        const patientsQuery = db
          .from("patients")
          .select("id, first_name, last_name, mrn");
        if (practiceId) patientsQuery.eq("practice_id", practiceId);
        else patientsQuery.in("id", uniqueIds);

        const { data: patientsData, error: patientsError } = await patientsQuery;

        if (patientsData) {
          const map = {};
          patientsData.forEach(p => { map[p.id] = p; });
          setPatientMap(map);
        }
      }

      setLoading(false);
    }

    load();
  }, [practiceId]);

  const getPatient = (pid) => (patients || []).find(p => p.id === pid);

  // Resolve display name: prefer DB lookup map, fall back to prop array
  const resolvePatientName = (note) => {
    const dbPt = patientMap[note.patient_id];
    if (dbPt) return `${dbPt.first_name} ${dbPt.last_name}`;
    const pt = getPatient(note.patient_id);
    return pt ? `${pt.first} ${pt.last}` : "Unknown patient";
  };

  // Resolve full patient record for navigation (MRN, chart link)
  const resolvePatientRecord = (note) => {
    const dbPt = patientMap[note.patient_id];
    if (dbPt) return { id: dbPt.id, first: dbPt.first_name, last: dbPt.last_name, mrn: dbPt.mrn };
    return getPatient(note.patient_id) || null;
  };

  const isPriority = (note) => {
    if (note.status !== "draft") return false;
    const d = new Date(note.visit_date + "T00:00:00");
    return (Date.now() - d.getTime()) > 2 * 24 * 60 * 60 * 1000;
  };

  const fmtDate = (d) =>
    new Date(d + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

  const visibleNotes = filter === "pending"
    ? notes.filter(n => n.status === "draft" || n.status === "unsigned")
    : notes;

  const pendingCount = notes.filter(n => n.status === "draft" || n.status === "unsigned").length;

  // Container fills available height; each panel scrolls independently
  const containerH = "calc(100vh - var(--header-h) - 48px)";

  return (
    <div style={{ display: "flex", height: containerH, overflow: "hidden", gap: 0, margin: "-4px 0 0 0" }}>

      {/* ── LEFT PANEL (35%) ── */}
      <div style={{
        width: "35%", flexShrink: 0,
        display: "flex", flexDirection: "column",
        borderRight: "1px solid var(--border)",
        overflow: "hidden",
      }}>
        {/* Header */}
        <div style={{ padding: "4px 0 12px 0", flexShrink: 0, borderBottom: "1px solid var(--border)" }}>
          <div style={{ fontSize: 16, fontWeight: 600, color: "var(--text-1)", marginBottom: 8 }}>Clinical Notes</div>
          {/* Filter toggle */}
          <div style={{ display: "flex", gap: 4 }}>
            {[
              { key: "all",     label: "All" },
              { key: "pending", label: `Drafts & Unsigned${pendingCount ? ` (${pendingCount})` : ""}` },
            ].map(opt => (
              <button
                key={opt.key}
                onClick={() => setFilter(opt.key)}
                style={{
                  padding: "4px 10px", borderRadius: 5, fontSize: 11, cursor: "pointer",
                  border: `1px solid ${filter === opt.key ? "var(--amber)" : "var(--border-2)"}`,
                  background: filter === opt.key ? "rgba(240,165,0,0.12)" : "transparent",
                  color: filter === opt.key ? "var(--amber)" : "var(--text-3)",
                  fontWeight: filter === opt.key ? 600 : 400,
                }}
              >{opt.label}</button>
            ))}
          </div>
        </div>

        {/* List */}
        <div style={{ flex: 1, overflowY: "auto" }}>
          {loading && (
            <div>
              {[...Array(5)].map((_, i) => <NoteCardSkeleton key={i} />)}
            </div>
          )}

          {!loading && visibleNotes.length === 0 && (
            <div className="empty-state" style={{ padding: "32px 16px" }}>
              <div className="empty-icon">◻</div>
              <div className="empty-text">
                {filter === "pending"
                  ? "No drafts or unsigned notes — all caught up!"
                  : "No notes yet — open a patient chart and click New Note to get started"}
              </div>
            </div>
          )}

          {!loading && visibleNotes.length > 0 && (
            <FixedSizeList
              height={Math.min(visibleNotes.length * 72, 560)}
              itemCount={visibleNotes.length}
              itemSize={72}
              width="100%"
            >
              {({ index, style }) => {
                const n        = visibleNotes[index];
                const pt       = getPatient(n.patient_id);
                const priority = isPriority(n);
                const isActive = selected?.id === n.id;
                return (
                  <div
                    style={{
                      ...style,
                      padding: "11px 14px",
                      cursor: "pointer",
                      borderBottom: "1px solid var(--border)",
                      borderLeft: `3px solid ${isActive ? "var(--teal)" : "transparent"}`,
                      background: isActive ? "var(--teal-lt)" : "transparent",
                      boxSizing: "border-box",
                    }}
                    onClick={() => setSelected(n)}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                      {priority && <span title="Draft — past 2 days" style={{ width: 7, height: 7, borderRadius: "50%", background: "var(--red)", flexShrink: 0 }} />}
                      <span style={{ fontSize: 13, fontWeight: 500, color: "var(--text-1)", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {resolvePatientName(n)}
                      </span>
                      <span className={`badge ${n.status === "signed" ? "badge-green" : "badge-amber"}`} style={{ fontSize: 10, flexShrink: 0 }}>
                        {n.status === "signed" ? "✓ Signed" : "◦ Draft"}
                      </span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}>
                      <span className={`badge ${getNoteTypeBadge(n.note_type)}`} style={{ fontSize: 10 }}>{n.note_type}</span>
                      <span className="text-xs text-muted">{fmtDate(n.visit_date)}{n.visit_time ? ` at ${fmt12(n.visit_time)}` : ""}</span>
                      <span className="text-xs text-muted" style={{ marginLeft: "auto", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{n.provider}</span>
                    </div>
                    {n.chief_complaint && (
                      <div className="text-xs text-muted" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontStyle: "italic" }}>
                        {n.chief_complaint}
                      </div>
                    )}
                  </div>
                );
              }}
            </FixedSizeList>
          )}
        </div>
      </div>

      {/* ── RIGHT PANEL (65%) ── */}
      <div style={{ flex: 1, overflowY: "auto", padding: "4px 0 0 24px", minWidth: 0 }}>
        {!selected ? (
          <div className="empty-state" style={{ height: "100%" }}>
            <div className="empty-icon" style={{ fontSize: 28 }}>◻</div>
            <div className="empty-text">Select a note to view</div>
          </div>
        ) : (() => {
          const pt = resolvePatientRecord(selected);
          return (
            <div className="fade-in">
              {/* Detail header */}
              <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 20, gap: 12 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 16, fontWeight: 600, color: "var(--text-1)", marginBottom: 6 }}>
                    {resolvePatientName(selected)}
                    {pt?.mrn && <span className="text-muted" style={{ fontWeight: 400, fontSize: 13, marginLeft: 8 }}>{pt.mrn}</span>}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <span className={`badge ${getNoteTypeBadge(selected.note_type)}`}>{selected.note_type}</span>
                    <span className="text-sm text-muted">{fmtDate(selected.visit_date)}</span>
                    <span className="text-muted">·</span>
                    <span className="text-sm text-muted">{selected.provider}</span>
                    <span className={`badge ${selected.status === "signed" ? "badge-teal" : "badge-amber"}`}>
                      {selected.status === "signed" ? "✓ Signed" : "◦ Draft"}
                    </span>
                  </div>
                </div>
                <div style={{ flexShrink: 0 }}>
                  {selected.status !== "signed" ? (
                    <button
                      className="btn btn-primary btn-sm"
                      disabled={!pt}
                      onClick={() => pt && onPatientSelect(pt, "notes")}
                    >
                      Open in Chart →
                    </button>
                  ) : (
                    <button
                      className="btn btn-secondary btn-sm"
                      disabled={!pt}
                      onClick={() => pt && onPatientSelect(pt, "notes")}
                    >
                      Amend
                    </button>
                  )}
                </div>
              </div>

              {/* Chief complaint */}
              {selected.chief_complaint && (
                <div style={{
                  background: "var(--navy-3)", border: "1px solid var(--border)",
                  borderRadius: "var(--radius)", padding: "10px 14px", marginBottom: 16,
                  borderLeft: "3px solid var(--teal)",
                }}>
                  <div className="field-label" style={{ marginBottom: 3 }}>Chief Complaint</div>
                  <div style={{ fontSize: 13, color: "var(--text-2)", fontStyle: "italic" }}>
                    {selected.chief_complaint}
                  </div>
                </div>
              )}

              {/* Note body */}
              <div className="card" style={{ marginBottom: 16 }}>
                <div className="card-header">
                  <span className="card-title">Note</span>
                  {selected.status === "signed" && (
                    <span style={{ marginLeft: "auto", fontSize: 11, color: "var(--text-3)" }}>🔒 Read-only · Signed</span>
                  )}
                </div>
                <div className="card-body">
                  {selected.body ? (
                    <div style={{ fontSize: 13, color: "var(--text-2)", lineHeight: 1.75, whiteSpace: "pre-wrap" }}>
                      {selected.body}
                    </div>
                  ) : (
                    <div className="text-muted text-sm" style={{ fontStyle: "italic" }}>No note body recorded.</div>
                  )}
                </div>
              </div>

              {/* CPT + ICD-10 codes */}
              {(selected.cpt_codes?.length > 0 || selected.icd10_codes?.length > 0) && (
                <div className="grid-2" style={{ gap: 12, marginBottom: 16 }}>
                  {selected.cpt_codes?.length > 0 && (
                    <div className="card">
                      <div className="card-header"><span className="card-title">CPT Codes</span><span className="badge badge-gray" style={{ marginLeft: "auto" }}>{selected.cpt_codes.length}</span></div>
                      <div className="card-body" style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                        {selected.cpt_codes.map(c => (
                          <span key={c.code} className="tag" title={c.desc}>{c.code}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {selected.icd10_codes?.length > 0 && (
                    <div className="card">
                      <div className="card-header"><span className="card-title">ICD-10 Codes</span><span className="badge badge-gray" style={{ marginLeft: "auto" }}>{selected.icd10_codes.length}</span></div>
                      <div className="card-body" style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                        {selected.icd10_codes.map(c => (
                          <span key={c.code} className="tag" title={c.desc}>{c.code}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })()}
      </div>
    </div>
  );
}
